/**
 * Table tennis court SVGs — pickleball blue combo; larger table, same outer viewBox.
 */
import {
  TABLE_TENNIS_SURROUND_BLUE,
  TABLE_TENNIS_TABLE_BLUE,
} from "../config/tableTennisCourtColour";

const LINE = "#ffffff";
const NET = "#e85d4a";
const NET_EDGE = "#fafaf8";
/** Slightly deeper wash for kitchen-style contrast (matches pickleball KITCHEN_WASH base). */
const TABLE_SHADE = "rgba(18, 48, 78, 0.22)";

function TableDefs({ idPrefix }) {
  return (
    <defs>
      <linearGradient id={`${idPrefix}-table`} x1="0" y1="0" x2="0.06" y2="1">
        <stop offset="0%" stopColor="#4a82b0" />
        <stop offset="40%" stopColor={TABLE_TENNIS_TABLE_BLUE} />
        <stop offset="100%" stopColor="#2f5c82" />
      </linearGradient>
      <linearGradient id={`${idPrefix}-gloss`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#6a9cc4" stopOpacity="0.22" />
        <stop offset="55%" stopColor={TABLE_TENNIS_TABLE_BLUE} stopOpacity="0.04" />
        <stop offset="100%" stopColor="#2f5c82" stopOpacity="0" />
      </linearGradient>
    </defs>
  );
}

function TableSurface({
  idPrefix,
  left,
  top,
  tableW,
  tableH,
  midPrimary,
  midSecondary,
}) {
  return (
    <>
      <rect
        x={left - 1}
        y={top + 1.5}
        width={tableW + 2}
        height={tableH + 2}
        rx={4}
        fill="#000"
        opacity="0.18"
      />
      <rect
        x={left}
        y={top}
        width={tableW}
        height={tableH}
        rx={3.5}
        fill={`url(#${idPrefix}-table)`}
        stroke={LINE}
        strokeWidth={1.75}
      />
      <rect
        x={left}
        y={top}
        width={tableW}
        height={tableH}
        rx={3.5}
        fill={TABLE_SHADE}
        pointerEvents="none"
      />
      <rect
        x={left}
        y={top}
        width={tableW}
        height={tableH}
        rx={3.5}
        fill={`url(#${idPrefix}-gloss)`}
        pointerEvents="none"
      />
      <line
        {...midPrimary}
        stroke={NET}
        strokeWidth={3.2}
        strokeLinecap="round"
      />
      <line
        {...midPrimary}
        stroke={NET_EDGE}
        strokeWidth={0.9}
        strokeLinecap="round"
        opacity="0.85"
      />
      <line
        {...midSecondary}
        stroke={LINE}
        strokeWidth={1.35}
        strokeDasharray="5 3.5"
        opacity="0.92"
      />
    </>
  );
}

/**
 * Portrait — viewBox unchanged (200×439); tighter insets so the table reads larger.
 */
export function PortraitTableTennisSvg() {
  const w = 200;
  const h = 439;
  const pad = 12;
  const left = pad;
  const top = pad + 12;
  const tableW = w - pad * 2;
  const tableH = h - pad * 2 - 24;
  const midY = top + tableH / 2;
  const midX = left + tableW / 2;
  const idPrefix = "ttp-blue";
  return (
    <svg
      className="court-svg"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <TableDefs idPrefix={idPrefix} />
      <rect width={w} height={h} fill={TABLE_TENNIS_SURROUND_BLUE} />
      <rect width={w} height={h} fill={TABLE_TENNIS_TABLE_BLUE} opacity="0.35" />
      <TableSurface
        idPrefix={idPrefix}
        left={left}
        top={top}
        tableW={tableW}
        tableH={tableH}
        midPrimary={{
          x1: left,
          y1: midY,
          x2: left + tableW,
          y2: midY,
        }}
        midSecondary={{
          x1: midX,
          y1: top,
          x2: midX,
          y2: top + tableH,
        }}
      />
    </svg>
  );
}

/**
 * Landscape — viewBox unchanged (439×200); tighter insets so the table reads larger.
 */
export function LandscapeTableTennisSvg() {
  const w = 439;
  const h = 200;
  const pad = 12;
  const left = pad + 12;
  const top = pad;
  const tableW = w - pad * 2 - 24;
  const tableH = h - pad * 2;
  const midX = left + tableW / 2;
  const midY = top + tableH / 2;
  const idPrefix = "ttl-blue";
  return (
    <svg
      className="court-svg"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <TableDefs idPrefix={idPrefix} />
      <rect width={w} height={h} fill={TABLE_TENNIS_SURROUND_BLUE} />
      <rect width={w} height={h} fill={TABLE_TENNIS_TABLE_BLUE} opacity="0.35" />
      <TableSurface
        idPrefix={idPrefix}
        left={left}
        top={top}
        tableW={tableW}
        tableH={tableH}
        midPrimary={{
          x1: midX,
          y1: top,
          x2: midX,
          y2: top + tableH,
        }}
        midSecondary={{
          x1: left,
          y1: midY,
          x2: left + tableW,
          y2: midY,
        }}
      />
    </svg>
  );
}
